import * as v from 'valibot';

export const RunStatus = v.picklist([
  'pending',
  'running',
  'awaiting_approval',
  'paused',
  'completed',
  'failed',
  'cancelled',
]);
export type RunStatus = v.InferOutput<typeof RunStatus>;

/**
 * One execution of a workflow. A run pins the workflow revision it started
 * against, so editing a workflow mid-flight never changes what an in-flight run
 * is doing — the config repo stays the source of truth for *new* runs.
 */
export const WorkflowRun = v.object({
  id: v.string(),
  workflowId: v.string(),
  revision: v.pipe(v.number(), v.integer()),
  status: RunStatus,
  /** Reef user id when a human started it; `null` for system/webhook triggers. */
  triggeredByUserId: v.nullable(v.string()),
  triggerType: v.string(),
  triggerPayload: v.optional(v.unknown()),
  createdAt: v.string(),
  updatedAt: v.string(),
  /** Set when this run was forked out of another run's step conversation. */
  forkedFrom: v.optional(
    v.nullable(
      v.object({
        runId: v.string(),
        stepId: v.string(),
        /** Durable-stream offset the fork was cut at. */
        offset: v.string(),
        forkedByUserId: v.string(),
        forkedAt: v.string(),
      }),
    ),
  ),
});
export type WorkflowRun = v.InferOutput<typeof WorkflowRun>;

export const StepRun = v.object({
  id: v.string(),
  runId: v.string(),
  stepId: v.string(),
  /**
   * The Flue agent instance id backing this step. Deterministic — see
   * `conversationIdFor` — so a webhook, a scheduler, and a chat client all
   * address the same durable conversation without a lookup.
   */
  conversationId: v.string(),
  status: v.string(),
  spendCents: v.optional(v.number(), 0),
  startedAt: v.nullable(v.string()),
  finishedAt: v.nullable(v.string()),
  error: v.optional(v.nullable(v.string())),
});
export type StepRun = v.InferOutput<typeof StepRun>;

/**
 * The durable address of a step's chat. Chatting with step 4 three days later
 * means POSTing to this id; Flue resolves it to the same Durable Object with the
 * same history and persisted state.
 */
export function conversationIdFor(runId: string, stepId: string): string {
  return `${runId}--${stepId}`;
}

export function parseConversationId(conversationId: string): { runId: string; stepId: string } | null {
  const index = conversationId.indexOf('--');
  if (index <= 0) return null;
  return {
    runId: conversationId.slice(0, index),
    stepId: conversationId.slice(index + 2),
  };
}

export const ForkRequest = v.object({
  runId: v.string(),
  stepId: v.string(),
  /**
   * Durable-stream offset to cut at. Omit to fork the whole conversation as it
   * stands right now.
   */
  offset: v.optional(v.string()),
  /** Optional new prompt delivered to the forked step as its first message. */
  message: v.optional(v.string()),
  /** Human label shown in the run list. */
  label: v.optional(v.string()),
  /** Fork only this step, or this step plus everything nested beneath it. */
  scope: v.optional(v.picklist(['step', 'subtree']), 'subtree'),
});
export type ForkRequest = v.InferOutput<typeof ForkRequest>;
